import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { IgCardControl } from "@/components/content/IgCardControl";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Link as LinkIcon, Upload, ChevronDown, Wand2, Ban, X as XIcon, CheckCircle2, Activity, Pencil, Save, CalendarPlus, Copy, FolderOpen, Loader2, FileText, Download, Image as ImageIcon, Plus, Users, Settings2 } from "lucide-react";
import { useAppState } from "@/state/AppStateProvider";
import { SourcesPicker, assembleSourcesContext } from "@/components/content/SourcesPicker";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ApprovalActions } from "@/components/content/ApprovalActions";
import { ScheduleAssetDialog } from "@/components/content/ScheduleAssetDialog";
import { RevisionConsole } from "@/components/content/RevisionConsole";
import { ReviseAgentPanel } from "@/components/content/ReviseAgentPanel";
import { PlatformPreviewPanel } from "@/components/content/PlatformPreviewPanel";
import { AgentAtWork } from "@/components/content/AgentAtWork";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { createInitialModelSettings, resolveRunModelSettings, resolveDefaultModel } from "@/lib/modelProviderSettings";
import { ModelPicker } from "@/components/content/ModelPicker";
import { RecipePicker } from "@/components/content/RecipePicker";
import { applyCreateTemplate } from "@/lib/createTemplates";
import { normalizeRecipeDiscovery } from "@/lib/discoveryOptions";
import { attachRunImagePrompt, getAssetCoverPrompt } from "@/lib/imagePromptAssets";
import { imageAspectForPlatform } from "@/lib/mediaAspect";
import { getPlatformPreviewMeta, getPostReviewWorkspace } from "@/lib/postReviewWorkspace";
import { writeClipboardText } from "@/lib/clipboard";
import { describeResearchMode, RESEARCH_MODE_OPTIONS } from "@/lib/researchModes";
import { calculateRunProgress, getExecutionReportPath, getVisibleReviewDeliverables } from "@/lib/createRunProgress";

const IMAGE_TEXT_MODE_OPTIONS = [
  { id: "auto", label: "Let agent decide", hint: "Best default. Adds designed text only when it helps the post." },
  { id: "off", label: "No text in image", hint: "Clean scene only. Best for free/fallback image models." },
  { id: "designed", label: "Design text into image", hint: "For GPT Images, Nano Banana, and other capable image models." },
];

const IMAGE_TEXT_MODE_SUMMARY = {
  auto: "agent decides text",
  off: "no image text",
  designed: "designed image text",
};

const PLATFORM_CARDS = [
  { id: "linkedin",       label: "LinkedIn",    brand: "linkedin", hint: "Long-form authority post", color: "#0A66C2" },
  { id: "x",              label: "X",           brand: "x",        hint: "Threaded breakdown",       color: "#111827" },
  { id: "youtube_shorts", label: "Shorts",      brand: "youtube",  hint: "45s vertical script",      color: "#FF0033" },
  { id: "instagram",      label: "Instagram",   brand: "instagram", hint: "Carousel or reel",        color: "#E1306C" },
  { id: "threads",        label: "Threads",     brand: "threads",  hint: "Conversational cut",       color: "#111827" },
  { id: "facebook",       label: "Facebook",    brand: "facebook", hint: "Community post",           color: "#1877F2" },
  { id: "bluesky",        label: "Bluesky",     brand: "bluesky",  hint: "Short skeet",              color: "#1185FE" },
];

export default function Create() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== "light";
  const [search] = useSearchParams();
  const { state, startAgentRun, advanceRun, updateAsset, approveAsset, rejectAsset, saveCreateTemplate, deleteCreateTemplate } = useAppState();
  const modelSettings = state.modelSettings?.providers?.length ? state.modelSettings : createInitialModelSettings(state.modelSettings || {});
  const defaultRunModel = useMemo(() => resolveRunModelSettings(modelSettings), [modelSettings]);

  const [prompt, setPrompt] = useState(search.get("prompt") || "");
  const [platforms, setPlatforms] = useState(["linkedin", "x"]);
  // Brand recipe: which brand + voice + one writing style. Defaults self-fill
  // from the active brand via <RecipePicker/>.
  const [recipe, setRecipe] = useState({ brandId: null, voiceId: null, styleSkillId: "" });
  const [activeRunId, setActiveRunId] = useState(null);
  const [runTab, setRunTab] = useState("posts");

  // Advanced (collapsed by default)
  const [optionsOpen, setOptionsOpen] = useState(false);
  // Model VERSION for this run. The provider is your Settings default; the pill
  // only switches the version, resetting when the default provider changes.
  const [modelVersion, setModelVersion] = useState(() => resolveDefaultModel(modelSettings).model);
  const [researchMode, setResearchMode] = useState(defaultRunModel.researchMode);
  const [researchProvider, setResearchProvider] = useState("free"); // free | ollama | browser
  const [researchFocus, setResearchFocus] = useState("general"); // general | academic
  // Per-post Instagram card override (blank = use the brand default).
  const [igCard, setIgCard] = useState({ theme: "", card_format: "" });
  const [notes, setNotes] = useState("");
  // Per-post target audience (optional). Engine-supported (`audience` flows into
  // the content plan); the intent extractor infers it from the prompt when blank.
  const [audience, setAudience] = useState("");
  // Grounding sources: pasted URLs and uploaded docs, each already extracted to
  // text by the backend. [{id, kind:"url"|"file", title, url, text, words, truncated}]
  const [sources, setSources] = useState([]);
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");

  // Cover image option: "generate" | "upload" | "none"
  const [imageMode, setImageMode] = useState("generate");
  const [imageTextMode, setImageTextMode] = useState("auto");
  const [uploadedImage, setUploadedImage] = useState(null); // data URI
  const [uploadedName, setUploadedName] = useState("");

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large - keep it under 8MB");
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result);
      setUploadedName(file.name);
      setImageMode("upload");
      toast.success(`${file.name} ready`);
    };
    reader.onerror = () => toast.error("Could not load image");
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onDropImage = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result);
      setUploadedName(file.name);
      setImageMode("upload");
      toast.success(`${file.name} ready`);
    };
    reader.readAsDataURL(file);
  };

  const togglePlatform = (p) => setPlatforms((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));

  const defaultProvider = useMemo(() => {
    const id = resolveDefaultModel(modelSettings).providerId;
    return modelSettings.providers.find((p) => p.id === id) || null;
  }, [modelSettings]);
  // Keep the version in sync when the default provider changes (in Settings).
  useEffect(() => {
    setModelVersion(resolveDefaultModel(modelSettings).model);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultProvider?.id]);
  const runModel = useMemo(
    () => resolveRunModelSettings(modelSettings, { providerId: defaultProvider?.id, modelName: modelVersion, researchMode }),
    [modelSettings, defaultProvider, modelVersion, researchMode],
  );
  const activeRun = useMemo(
    () => state.runs.find((run) => run.id === activeRunId) || null,
    [state.runs, activeRunId],
  );
  const activeAssets = useMemo(() => {
    if (!activeRun?.asset_ids?.length) return [];
    return state.assets.filter((asset) => activeRun.asset_ids.includes(asset.id));
  }, [state.assets, activeRun]);
  const activeAssetsWithPrompt = useMemo(() => attachRunImagePrompt(activeAssets), [activeAssets]);
  const activeDeliverables = useMemo(
    () => activeAssetsWithPrompt.filter((asset) => !["research_brief", "image_prompt", "execution_report"].includes(asset.asset_type)),
    [activeAssetsWithPrompt],
  );
  const activeSupporting = useMemo(
    () => activeAssets.filter((asset) => ["research_brief", "image_prompt"].includes(asset.asset_type)),
    [activeAssets],
  );
  const runProgress = useMemo(() => {
    return calculateRunProgress(activeRun || {});
  }, [activeRun]);

  // The recipe's resolved brand + voice + style names, for the plan summary.
  const recipeNames = useMemo(() => {
    const brand = (state.brands || []).find((b) => b.id === recipe.brandId)
      || (state.brands || []).find((b) => b.id === state.activeBrandId)
      || (state.brands || [])[0] || null;
    const voice = (brand?.voices || []).find((v) => v.id === recipe.voiceId)
      || (brand?.voices || []).find((v) => v.is_primary) || (brand?.voices || [])[0] || null;
    const style = (state.skills || []).find((s) => s.id === recipe.styleSkillId) || null;
    return { brandName: brand?.name || "your brand", voiceName: voice?.name || "brand voice", styleName: style?.name || null };
  }, [state.brands, state.activeBrandId, state.skills, recipe]);

  const selectedPlatformLabels = useMemo(
    () => platforms.map((p) => PLATFORM_CARDS.find((x) => x.id === p)?.label || p),
    [platforms],
  );
  const mediaSummary = imageMode === "generate"
    ? `Generate image · ${IMAGE_TEXT_MODE_SUMMARY[imageTextMode] || IMAGE_TEXT_MODE_SUMMARY.auto}`
    : imageMode === "upload"
      ? uploadedName || "Upload image"
      : "No image";
  const toneSummary = recipeNames.styleName ? `${recipeNames.voiceName} · ${recipeNames.styleName}` : recipeNames.voiceName;
  const optionsSummary = [toneSummary, mediaSummary, audience.trim() ? `for ${audience.trim()}` : ""]
    .filter(Boolean)
    .join(" · ");
  const createTemplates = state.createTemplates || [];
  const suggestedTemplateName = prompt.split("\n")[0].trim().slice(0, 46) || "New create template";

  const currentTemplateDraft = () => ({
    prompt,
    platforms,
    recipe,
    researchMode,
    imageMode,
    imageTextMode,
    uploadedName,
    url: recipe.destinationUrl || "",
    notes,
  });

  const onSaveTemplate = () => {
    if (!prompt.trim()) return toast.error("Add a prompt before saving a template");
    const template = saveCreateTemplate(currentTemplateDraft(), {
      name: templateName.trim() || suggestedTemplateName,
    });
    setTemplateName("");
    setTemplatePanelOpen(false);
    toast.success(`Saved template: ${template.name}`);
  };

  const onApplyTemplate = (templateId) => {
    const template = createTemplates.find((item) => item.id === templateId);
    if (!template) return;
    const next = applyCreateTemplate(template);
    setPrompt(next.prompt);
    setPlatforms(next.platforms);
    if (next.recipe) setRecipe(next.recipe);
    setResearchMode(next.researchMode);
    setImageMode(next.imageMode);
    setImageTextMode(next.imageTextMode || "auto");
    setUploadedImage(next.uploadedImage);
    setUploadedName(next.uploadedName);
    setNotes(next.notes);
    setPreflightOpen(false);
    setSetupStep("channels");
    toast.success(`Loaded template: ${template.name}`);
  };

  const onDeleteTemplate = (templateId) => {
    const template = createTemplates.find((item) => item.id === templateId);
    deleteCreateTemplate(templateId);
    toast.message(template ? `Removed template: ${template.name}` : "Template removed");
  };

  const onStart = () => {
    if (!prompt.trim()) return toast.error("Describe what you want to post first");
    if (platforms.length === 0) return toast.error("Pick at least one platform");
    if (imageMode === "upload" && !uploadedImage) return toast.error("Pick an image to upload, or switch to Generate / No image");
    const recipeDiscovery = normalizeRecipeDiscovery(recipe);
    const id = startAgentRun({
      title: prompt.split("\n")[0].slice(0, 80),
      prompt,
      model: runModel.providerId,
      model_provider: runModel.providerId,
      model_label: runModel.providerLabel,
      model_name: runModel.modelName,
      model_configured: runModel.configured,
      platforms,
      // The brand recipe: brand + one of its voices + (optionally) one writing
      // style. Industry knowledge + critique are applied automatically by the brand.
      brand_id: recipe.brandId || undefined,
      voice_id: recipe.voiceId || undefined,
      style_skill_id: recipe.styleSkillId || undefined,
      // Composable layers on top of the format: intent + CTA + social SEO.
      goal: recipe.goal || undefined,
      cta: typeof recipe.cta === "boolean" ? recipe.cta : undefined,
      cta_type: recipe.cta === true ? (recipe.ctaType || undefined) : undefined,
      audience: audience.trim() || undefined,
      // DEPRECATED: `seo` is superseded by `discovery_mode` (the RecipePicker sets
      // discovery directly and never sets seo). Kept as dormant back-compat only.
      seo: typeof recipe.seo === "boolean" ? recipe.seo : undefined,
      discovery_mode: recipeDiscovery.discoveryMode,
      hashtag_mode: recipeDiscovery.hashtagMode,
      destination_url: recipe.destinationUrl?.trim() || undefined,
      skills_used: [recipeNames.voiceName, recipeNames.styleName].filter(Boolean),
      // Per-post IG card override (only when IG is a target and something is set).
      card_style: platforms.includes("instagram") && (igCard.theme || igCard.card_format)
        ? { theme: igCard.theme || undefined, card_format: igCard.card_format || undefined }
        : undefined,
      research_mode: runModel.researchMode,
      research_provider: researchProvider,
      research_focus: researchFocus,
      // Ground generation on the user's own material (added sources + notes),
      // already extracted to text, so niche/private subjects aren't hallucinated.
      context: [notes?.trim() || "", assembleSourcesContext(sources)].filter(Boolean).join("\n\n") || undefined,
      source_label: sources.length ? `${sources.length} source${sources.length > 1 ? "s" : ""}` : undefined,
      image_mode: imageMode,
      image_text_mode: imageTextMode,
      uploaded_image: imageMode === "upload" ? uploadedImage : null,
    });
    setActiveRunId(id);
    setRunTab("posts");
    toast.success("Generating with your connected model…");
  };

  // Start a fresh post: clear the per-post content + supporting materials so the
  // next post doesn't inherit the previous run's prompt, notes, sources, or image.
  // Sticky preferences (platforms + the brand recipe) are intentionally kept.
  const startNewPost = () => {
    setActiveRunId(null);
    setPrompt("");
    setNotes("");
    setAudience("");
    setSources([]);
    setUploadedImage(null);
    setUploadedName("");
    setImageMode("generate");
    setImageTextMode("auto");
    setIgCard({ theme: "", card_format: "" });
    setRunTab("posts");
  };

  useEffect(() => {
    if (!activeRun || activeRun.status !== "running") return;
    const timer = setInterval(() => advanceRun(activeRun.id), 1800);
    return () => clearInterval(timer);
  }, [activeRun, advanceRun]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-7" data-testid="create-page">
      <section
        className="console-panel relative overflow-hidden rounded-2xl p-5 shadow-[0_22px_70px_-44px_var(--chrome-shadow)]"
        data-testid="create-command-hero"
      >
        <div aria-hidden className="console-glow-1 pointer-events-none absolute inset-0 opacity-95" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[color:var(--text-2)]/80">Create</p>
            <h1 className="mt-2 text-2xl font-bold leading-tight tracking-[-0.03em] text-[color:var(--text-1)] sm:text-[30px]">Create platform-ready social posts.</h1>
            <p className="mt-2 max-w-3xl text-sm leading-[1.65] text-[color:var(--text-2)]">Start with what you want to say. The agent will ask for the missing pieces, show the plan, then generate drafts for approval.</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-[color:var(--accent-cyan)]/12 px-3 py-1.5 text-[12px] font-medium text-[color:var(--text-1)]" style={{ borderColor: "rgba(143, 224, 74,0.22)" }}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Approval gated
          </span>
        </div>
      </section>

      <div className="w-full">
        <div className="space-y-5">
          <div
            className="console-panel relative overflow-hidden rounded-2xl p-5 shadow-[0_24px_80px_-38px_var(--chrome-shadow)] sm:p-6"
            data-testid="create-agent-shell"
          >
            <div aria-hidden className="console-glow-2 pointer-events-none absolute inset-0 opacity-95" />
            <div className="relative flex flex-wrap items-start justify-between gap-3">
              <div>
                <Label htmlFor="prompt" className="text-xs uppercase tracking-[0.18em] text-[color:var(--text-2)]">Describe what you want to post</Label>
                <p className="mt-1 max-w-2xl text-sm text-[color:var(--text-2)]">Give the agent the topic, angle, audience, CTA, or any context you already have.</p>
              </div>
              <ModelPicker
                provider={defaultProvider}
                value={modelVersion}
                onChange={setModelVersion}
                variant={isDark ? "dark" : "light"}
              />
            </div>
            {activeRun ? (
              <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="relative mt-4 rounded-xl border bg-[color:var(--surface-1)] p-4 text-foreground sm:p-5"
                style={{ borderColor: "var(--border-subtle)" }}
                data-testid="create-agent-active-run"
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Agent run</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={startNewPost}
                    data-testid="create-new-post"
                    className="border-[color:var(--border-subtle)] bg-card text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)]"
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> New post
                  </Button>
                </div>
                {(activeRun?.prompt || activeRun?.grounding) && (
                  <details className="mb-3 rounded-lg border bg-[color:var(--surface-2)] px-3 py-2 text-[12.5px]" style={{ borderColor: "rgba(160, 168, 162,0.9)" }} data-testid="create-run-inputs">
                    <summary className="cursor-pointer select-none font-medium text-[color:var(--text-2)]">What you sent the agent</summary>
                    {activeRun?.prompt && (
                      <div className="mt-2">
                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Prompt</p>
                        <p className="mt-0.5 whitespace-pre-wrap text-[color:var(--text-1)]">{activeRun.prompt}</p>
                      </div>
                    )}
                    {activeRun?.grounding && (
                      <div className="mt-2">
                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Source facts &amp; uploaded material</p>
                        <pre className="mt-0.5 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-card p-2 font-sans text-[12px] text-[color:var(--text-2)]" style={{ border: "1px solid rgba(160, 168, 162,0.7)" }}>{activeRun.grounding}</pre>
                      </div>
                    )}
                  </details>
                )}
                <LiveRunStage
                  run={activeRun}
                  progress={runProgress}
                  deliverables={activeDeliverables}
                  supporting={activeSupporting}
                  assets={activeAssets}
                  runTab={runTab}
                  setRunTab={setRunTab}
                  updateAsset={updateAsset}
                  approveAsset={approveAsset}
                  rejectAsset={rejectAsset}
                />
              </motion.div>
            ) : (
              <div className="relative mt-4 space-y-3" data-testid="create-agent-window">
                <div className="overflow-hidden rounded-xl border bg-[color:var(--surface-1)]" style={{ borderColor: "var(--border-subtle)" }}>
                  <Textarea
                    id="prompt"
                    data-testid="create-prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={4}
                    placeholder="e.g. Write a LinkedIn post about why phase-aware skills matter for AI operators. Use my Adam voice. End with a soft DM CTA."
                    className="min-h-[140px] w-full resize-none border-0 bg-transparent p-4 text-[16px] font-medium leading-[1.6] text-1 shadow-none placeholder:text-muted-foreground/55 focus-visible:ring-2 focus-visible:ring-[color:var(--accent-cyan)]/40 sm:text-[17px]"
                  />
                </div>

                <ConfigSection title="Where should this post?" summary={selectedPlatformLabels.length ? selectedPlatformLabels.join(", ") : "Choose one or more channels"}>
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-7" data-testid="create-platforms">
                    {PLATFORM_CARDS.map((p) => {
                      const active = platforms.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePlatform(p.id)}
                          data-testid={`create-platform-${p.id}`}
                          aria-label={p.label}
                          title={p.label}
                          className={cn(
                            "group relative flex h-14 items-center justify-center overflow-hidden rounded-xl border transition-all duration-200",
                            active
                              ? "bg-card text-foreground shadow-[0_14px_34px_-24px_var(--platform-color)]"
                              : "bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:bg-card hover:shadow-[0_10px_24px_-22px_rgba(18, 22, 20,0.55)]",
                          )}
                          style={{
                            "--platform-color": p.color,
                            borderColor: active ? p.color : "rgba(160, 168, 162,0.9)",
                            backgroundColor: active ? `${p.color}0F` : undefined,
                          }}
                        >
                          {active && (
                            <span aria-hidden className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full text-white shadow-[0_8px_18px_-8px_rgba(143, 224, 74,0.8)]" style={{ backgroundColor: p.color }}>
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </span>
                          )}
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_1px_4px_rgba(0,0,0,0.35)] ring-1 ring-black/10">
                            <BrandLogo brand={p.brand} color={p.color} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </ConfigSection>

                {optionsOpen && createPortal(
                  <div className="fixed inset-0 z-50 flex justify-end" data-testid="create-setup-drawer">
                    <div className="absolute inset-0 bg-black/55" aria-hidden onClick={() => setOptionsOpen(false)} />
                    <div className="relative flex h-full w-full max-w-md flex-col border-l shadow-2xl" style={{ backgroundColor: "var(--surface-1)", borderColor: "var(--border-subtle)" }} role="dialog" aria-label="Post options">
                      <div className="flex items-center justify-between gap-3 border-b px-4 py-3.5" style={{ borderColor: "var(--border-subtle)" }}>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[color:var(--text-1)]">Post options</p>
                          <p className="truncate text-[12px] text-[color:var(--text-3)]">{optionsSummary || "Voice, image, CTA, discovery, hashtags"}</p>
                        </div>
                        <button type="button" onClick={() => setOptionsOpen(false)} aria-label="Close options" className="shrink-0 rounded-md border p-1.5 text-[color:var(--text-2)] hover:text-[color:var(--text-1)]" style={{ borderColor: "var(--border-subtle)" }}>
                          <XIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="create-setup-body">
                <ConfigSection title="Should this include an image?" summary={mediaSummary}>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3" data-testid="create-image-options">
                    <ImageOption
                      dark
                      active={imageMode === "generate"}
                      onClick={() => setImageMode("generate")}
                      testId="create-image-generate"
                      icon={Wand2}
                      accent="#8FE04A"
                      label="Generate with AI"
                      hint="Branded cover from the topic."
                    />
                    <ImageOption
                      dark
                      active={imageMode === "upload"}
                      onClick={() => { setImageMode("upload"); if (!uploadedImage) document.getElementById("create-image-input")?.click(); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={onDropImage}
                      testId="create-image-upload"
                      icon={Upload}
                      accent="#D97706"
                      label="Upload my own"
                      hint={uploadedName ? `Selected: ${uploadedName}` : "Drag here or browse."}
                    >
                      {uploadedImage && (
                        <div className="mt-3 overflow-hidden rounded-md border" style={{ borderColor: "var(--border-subtle)" }}>
                          <img src={uploadedImage} alt="Upload preview" className="block h-20 w-full object-cover" />
                        </div>
                      )}
                      {imageMode === "upload" && uploadedImage && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setUploadedImage(null); setUploadedName(""); }}
                          className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-1"
                          data-testid="create-image-clear"
                        >
                          <XIcon className="h-3 w-3" /> Clear image
                        </button>
                      )}
                      <input id="create-image-input" type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="create-image-input" />
                    </ImageOption>
                    <ImageOption
                      dark
                      active={imageMode === "none"}
                      onClick={() => setImageMode("none")}
                      testId="create-image-none"
                      icon={Ban}
                      accent="#64748B"
                      label="No image"
                      hint="Ship text-only posts."
                    />
                  </div>
                  {imageMode === "generate" && (
                    <div
                      className="mt-3 grid gap-2 rounded-xl border bg-[color:var(--surface-2)] p-3 sm:grid-cols-[minmax(0,1fr)_220px]"
                      style={{ borderColor: "rgba(160, 168, 162,0.9)" }}
                      data-testid="create-image-text-mode-panel"
                    >
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Image text</p>
                        <p className="mt-1 text-[12px] leading-[1.5] text-muted-foreground">
                          Decide whether the image prompt should include creative text designed into the image.
                        </p>
                      </div>
                      <Select value={imageTextMode} onValueChange={setImageTextMode}>
                        <SelectTrigger className="h-10 border-[color:var(--border-subtle)] bg-card text-foreground" data-testid="create-image-text-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {IMAGE_TEXT_MODE_OPTIONS.map((option) => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="sm:col-span-2 text-[11px] leading-[1.5] text-muted-foreground">
                        {IMAGE_TEXT_MODE_OPTIONS.find((option) => option.id === imageTextMode)?.hint || IMAGE_TEXT_MODE_OPTIONS[0].hint}
                      </p>
                    </div>
                  )}
                </ConfigSection>

                <ConfigSection title="Brand, voice & style" summary={toneSummary}>
                  <div className="mb-4 space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" /> Audience <span className="text-muted-foreground/70">(optional — inferred from your prompt if blank)</span>
                    </Label>
                    <Input
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      placeholder="e.g. small business owners, first-time home buyers, local dentists"
                      className="border-white/15 bg-white/10 text-slate-100 placeholder:text-muted-foreground"
                      data-testid="create-audience"
                    />
                  </div>
                  <RecipePicker value={recipe} onChange={setRecipe} variant="dark" />
                </ConfigSection>

                {platforms.includes("instagram") && (
                  <ConfigSection title="Instagram card" summary={igCard.card_format || igCard.theme ? "Custom" : "Brand default"}>
                    <p className="mb-2 text-[11.5px] text-[color:var(--text-3)]">Single = one card · Carousel = swipeable slides. <Link to="/brands" className="text-[color:var(--accent-cyan)] hover:underline">Brand default</Link> applies otherwise.</p>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Select value={igCard.card_format || "default"} onValueChange={(v) => setIgCard((c) => ({ ...c, card_format: v === "default" ? "" : v }))}>
                        <SelectTrigger className="h-9 border-white/15 bg-white/10 text-white" data-testid="create-ig-format"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Format: brand default</SelectItem>
                          <SelectItem value="single">Single card</SelectItem>
                          <SelectItem value="carousel">Carousel (multi-slide)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={igCard.theme || "default"} onValueChange={(v) => setIgCard((c) => ({ ...c, theme: v === "default" ? "" : v }))}>
                        <SelectTrigger className="h-9 border-white/15 bg-white/10 text-white" data-testid="create-ig-theme"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Theme: brand default</SelectItem>
                          {["auto", "midnight", "indigo", "plum", "forest", "ember"].map((t) => (
                            <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </ConfigSection>
                )}

                <ConfigSection title="Research & grounding" summary={describeResearchMode(researchMode)}>
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-3" style={{ borderColor: "var(--border-subtle)" }}>
                      <div>
                        <p className="text-sm text-foreground">Research</p>
                        <p className="text-[11.5px] text-muted-foreground">{describeResearchMode(researchMode)}</p>
                      </div>
                      <Select value={researchMode} onValueChange={setResearchMode}>
                        <SelectTrigger className="w-[170px] border-[color:var(--border-subtle)] bg-card text-foreground" data-testid="create-research-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RESEARCH_MODE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {researchMode !== "none" && (
                      <div className="space-y-2 rounded-lg border border-dashed p-3" style={{ borderColor: "var(--border-subtle)" }} data-testid="create-research-pro">
                        <p className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Pro — search backend</p>
                        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-3" style={{ borderColor: "var(--border-subtle)" }}>
                          <div>
                            <p className="text-sm text-foreground">Research focus</p>
                            <p className="text-[11.5px] text-muted-foreground">{researchFocus === "academic" ? "Academic — peer-reviewed papers (OpenAlex, no key needed)." : "General web — wikis, news, guides, and articles."}</p>
                          </div>
                          <Select value={researchFocus} onValueChange={setResearchFocus}>
                            <SelectTrigger className="w-[150px] border-[color:var(--border-subtle)] bg-card text-foreground" data-testid="create-research-focus"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">General web</SelectItem>
                              <SelectItem value="academic">Academic papers</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {researchFocus === "general" && (
                          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-3" style={{ borderColor: "var(--border-subtle)" }}>
                            <div>
                              <p className="text-sm text-foreground">Research provider</p>
                              <p className="text-[11.5px] text-muted-foreground">{researchProvider === "browser" ? "Browser opens real pages — slower, reads JS/paywalled sites." : researchProvider === "ollama" ? "Ollama Web Search — richer snippets, uses your Ollama Cloud key." : "Free web search (Mojeek) — no key needed."}</p>
                            </div>
                            <Select value={researchProvider} onValueChange={setResearchProvider}>
                              <SelectTrigger className="w-[150px] border-[color:var(--border-subtle)] bg-card text-foreground" data-testid="create-research-provider"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="free">Free (no key)</SelectItem>
                                <SelectItem value="ollama">Ollama (your key)</SelectItem>
                                <SelectItem value="browser">Browser (reads pages)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><LinkIcon className="h-3 w-3" /> Sources <span className="text-muted-foreground">— URLs or upload PDF / Word / text</span></Label>
                      <SourcesPicker sources={sources} onChange={setSources} variant="dark" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Pencil className="h-3 w-3" /> Source facts to ground on <span className="text-muted-foreground">(optional)</span></Label>
                      <Textarea className="border-[color:var(--border-subtle)] bg-card text-foreground placeholder:text-muted-foreground" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Paste real facts, numbers, or notes the post must be based on — the agent treats this as the source of truth (it does NOT control formatting)." data-testid="create-notes" />
                    </div>
                  </div>
                </ConfigSection>
                      </div>
                      <div className="flex justify-end gap-2 border-t px-4 py-3" style={{ borderColor: "var(--border-subtle)" }}>
                        <Button onClick={() => setOptionsOpen(false)} className="h-9 px-5" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>Done</Button>
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}

            {!activeRun && (
              <>
              {(templatePanelOpen || createTemplates.length > 0) && (
                <section
                  className="relative mt-4 overflow-hidden rounded-xl border border-white/10 bg-white/[0.055] p-3"
                  data-testid="create-template-panel"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-1)]/80">Templates</p>
                      <p className="mt-1 text-[12px] text-slate-300">Save or reload repeat Create setups without leaving the agent window.</p>
                    </div>
                    {createTemplates.length > 0 && (
                      <Select onValueChange={onApplyTemplate}>
                        <SelectTrigger className="h-9 min-w-[240px] border-white/15 bg-white/10 text-white" data-testid="create-template-select">
                          <SelectValue placeholder="Load template" />
                        </SelectTrigger>
                        <SelectContent>
                          {createTemplates.map((template) => (
                            <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {templatePanelOpen && (
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center" data-testid="create-template-save-panel">
                      <Input
                        value={templateName}
                        onChange={(event) => setTemplateName(event.target.value)}
                        placeholder={suggestedTemplateName}
                        className="border-white/15 bg-white/10 text-white placeholder:text-muted-foreground"
                        data-testid="create-template-name"
                      />
                      <div className="flex gap-2">
                        <Button type="button" size="sm" onClick={onSaveTemplate} className="gap-1.5" data-testid="create-template-save-confirm" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
                          <Save className="h-3.5 w-3.5" /> Save
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setTemplatePanelOpen(false)} className="border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white" data-testid="create-template-save-cancel">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {createTemplates.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2" data-testid="create-template-list">
                      {createTemplates.slice(0, 4).map((template) => (
                        <div key={template.id} className="flex items-center overflow-hidden rounded-md border border-white/10 bg-white/[0.075]">
                          <button
                            type="button"
                            onClick={() => onApplyTemplate(template.id)}
                            className="max-w-[240px] truncate px-2.5 py-1.5 text-left text-[12px] font-medium text-slate-100 hover:bg-white/10"
                            data-testid={`create-template-load-${template.id}`}
                          >
                            {template.name}
                          </button>
                          <button
                            type="button"
                            onClick={() => onDeleteTemplate(template.id)}
                            className="border-l border-white/10 px-2 py-1.5 text-muted-foreground hover:bg-white/10 hover:text-white"
                            aria-label={`Delete ${template.name}`}
                            data-testid={`create-template-delete-${template.id}`}
                          >
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}

            <div className="relative mt-5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-4">
              <button
                type="button"
                onClick={() => setOptionsOpen(true)}
                className="flex min-w-0 items-center gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-white/[0.06]"
                style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}
                data-testid="create-setup-open"
              >
                <Settings2 className="h-4 w-4 shrink-0 text-[color:var(--text-3)]" />
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-medium text-[color:var(--text-1)]">Post options</span>
                  <span className="block max-w-[240px] truncate text-[11px] text-[color:var(--text-3)]">{optionsSummary || "Voice, image, CTA, discovery"}</span>
                </span>
              </button>
              <span className="flex-1" />
              <Button
                variant="outline"
                className="border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white"
                onClick={() => {
                  setTemplateName(suggestedTemplateName);
                  setTemplatePanelOpen(true);
                }}
                data-testid="create-save-template"
              >
                Save as template
              </Button>
              <Button onClick={onStart} className="gap-1.5 h-10 px-5" data-testid="create-generate-button" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
                <Sparkles className="h-4 w-4" /> Generate
              </Button>
            </div>
              </>
            )}
          </div>
        </div>
      </div>

    </motion.div>
  );
}

// A titled, always-visible config block on the flat Create screen (replaces the
// old gated accordion step). Shows a live summary of the current selection.
function ConfigSection({ title, summary, children }) {
  return (
    <section className="rounded-xl border p-4" style={{ backgroundColor: "var(--surface-3)", borderColor: "var(--border-subtle)" }}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <span className="text-sm font-semibold text-[color:var(--text-1)]">{title}</span>
        {summary ? <span className="truncate text-[12.5px] font-medium text-[color:var(--text-2)]">{summary}</span> : null}
      </div>
      {children}
    </section>
  );
}

function LiveRunStage({ run, progress, deliverables, supporting, assets, runTab, setRunTab, updateAsset, approveAsset, rejectAsset }) {
  const isDone = run.status === "completed";
  const isFailed = run.status === "failed";
  const reportPath = getExecutionReportPath(run);
  const reviewDeliverables = getVisibleReviewDeliverables(run, deliverables);
  const reviewSupporting = isDone ? supporting : [];

  return (
    <section className="space-y-4" data-testid="create-live-stage">
      <AgentAtWork
        run={run}
        progress={progress}
        deliverableCount={deliverables.length}
        supportingCount={supporting.length}
        assets={assets}
        deliverables={deliverables}
        action={
          <Button asChild variant="outline" size="sm" className="border-white/15 bg-white/10 text-white hover:bg-white/15 hover:text-white">
            <Link to={reportPath}>Execution Report</Link>
          </Button>
        }
      />

      {isFailed ? (
        <RunFailurePanel message={run.error} />
      ) : (
        <ReviewWorkspace
          isDone={isDone}
          deliverables={reviewDeliverables}
          supporting={reviewSupporting}
          runTab={runTab}
          setRunTab={setRunTab}
          updateAsset={updateAsset}
          approveAsset={approveAsset}
          rejectAsset={rejectAsset}
        />
      )}
    </section>
  );
}

function RunFailurePanel({ message }) {
  return (
    <section
      className="rounded-xl border bg-red-50 p-5 shadow-[0_18px_48px_-38px_rgba(127,29,29,0.45)]"
      style={{ borderColor: "rgba(220,38,38,0.22)" }}
      data-testid="create-run-failed"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-card text-red-600" style={{ borderColor: "rgba(220,38,38,0.22)" }}>
          <Ban className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-500">No drafts created</p>
          <h3 className="mt-1 text-lg font-semibold tracking-[-0.02em] text-red-950">The agent stopped before output was saved.</h3>
          <p className="mt-1 text-sm leading-relaxed text-red-800">{message || "Check your provider settings and try the run again."}</p>
        </div>
      </div>
    </section>
  );
}

function ReviewWorkspace({ isDone, deliverables, supporting, runTab, setRunTab, updateAsset, approveAsset, rejectAsset }) {
  const [editingAssetId, setEditingAssetId] = useState(null);
  const editorRef = useRef(null);
  const readyCount = deliverables.filter((asset) => asset.status === "needs_review").length;
  const editingAsset = deliverables.find((asset) => asset.id === editingAssetId) || null;
  const reviewCopy = isDone
    ? readyCount > 0
      ? `${readyCount} ${readyCount === 1 ? "post" : "posts"} ready for review. Nothing publishes until approved.`
      : "All generated posts have a decision. Approved drafts are ready for scheduling or publishing workflow."
    : "Drafts will arrive here as the pipeline finishes.";

  const handleEditAsset = (assetId) => {
    setRunTab("posts");
    setEditingAssetId(assetId);
  };

  useEffect(() => {
    if (editingAssetId && !editingAsset) setEditingAssetId(null);
  }, [editingAssetId, editingAsset]);

  useEffect(() => {
    if (!editingAsset) return undefined;
    const timer = window.setTimeout(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      editorRef.current?.focus({ preventScroll: true });
    }, 90);
    return () => window.clearTimeout(timer);
  }, [editingAsset]);

  return (
    <section
      className="overflow-hidden rounded-xl border bg-card shadow-[0_18px_48px_-38px_rgba(18, 22, 20,0.45)]"
      style={{ borderColor: "rgba(18, 22, 20,0.12)" }}
      data-testid="create-agent-review"
    >
      <div className="border-b bg-[color:var(--surface-2)] px-4 py-4" style={{ borderColor: "rgba(160, 168, 162,0.82)" }}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Review queue</p>
            <h3 className="mt-1 text-xl font-semibold tracking-[-0.025em] text-foreground">Generated output</h3>
            <p className="mt-1 text-sm text-muted-foreground">{reviewCopy}</p>
          </div>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-[12px] font-medium",
              isDone ? "bg-[color:var(--accent-green)]/12 text-[color:var(--accent-green)]" : "bg-[color:var(--accent-blue)]/12 text-[color:var(--accent-cyan)]",
            )}
            style={{ borderColor: isDone ? "rgba(22,163,74,0.20)" : "rgba(18, 147, 79,0.20)" }}
          >
            {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Activity className="h-3.5 w-3.5" />}
            {isDone ? "Ready" : "Generating"}
          </span>
        </div>
        <Tabs value={runTab} onValueChange={setRunTab} className="mt-4">
          <TabsList className="bg-card p-1 gap-1 shadow-sm" data-testid="create-run-tabs">
            <TabsTrigger value="posts" className="data-[state=active]:bg-[#0C0F0E] data-[state=active]:text-white" data-testid="create-run-tab-posts">
              Posts ({deliverables.length})
            </TabsTrigger>
            <TabsTrigger value="materials" className="data-[state=active]:bg-[#0C0F0E] data-[state=active]:text-white" data-testid="create-run-tab-materials">
              Supporting materials ({supporting.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <AnimatePresence initial={false}>
        {editingAsset && (
          <PostEditorPanel
            key={editingAsset.id}
            asset={editingAsset}
            updateAsset={updateAsset}
            approveAsset={approveAsset}
            rejectAsset={rejectAsset}
            onClose={() => setEditingAssetId(null)}
            editorRef={editorRef}
          />
        )}
      </AnimatePresence>

      <div className="bg-[color:var(--surface-3)]/70 p-3 sm:p-4" data-testid="create-contained-output">
        {runTab === "posts" ? (
          <GeneratedPostsPanel
            deliverables={deliverables}
            editingAssetId={editingAssetId}
            onEditAsset={handleEditAsset}
            updateAsset={updateAsset}
            approveAsset={approveAsset}
            rejectAsset={rejectAsset}
          />
        ) : (
          <SupportingMaterialsPanel supporting={supporting} />
        )}
      </div>
    </section>
  );
}

function getAssetPostText(asset) {
  return [asset.hook, asset.body, asset.cta && asset.cta !== "\u2014" ? asset.cta : null, Array.isArray(asset.hashtags) ? asset.hashtags.join(" ") : null]
    .filter(Boolean)
    .join("\n\n");
}

async function copyGeneratedPost(asset) {
  const text = getAssetPostText(asset);
  if (!text) return toast.error("Nothing to copy");
  const copied = await writeClipboardText(text);
  copied ? toast.success("Post text copied") : toast.error("Could not copy post text");
}

async function copySupportingText(text, label = "Text") {
  if (!text) return toast.error("Nothing to copy");
  const copied = await writeClipboardText(text);
  copied ? toast.success(`${label} copied`) : toast.error(`Could not copy ${label.toLowerCase()}`);
}

function PostEditorPanel({ asset, updateAsset, approveAsset, rejectAsset, onClose, editorRef }) {
  const { reviseAsset, generateAssetCover, uploadAssetCover } = useAppState();
  const [draft, setDraft] = useState(() => makeDraftFromAsset(asset));
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverPrompt, setCoverPrompt] = useState(() => getAssetCoverPrompt(asset));
  const coverInputRef = useRef(null);

  const onGenerateCover = async () => {
    const p = (coverPrompt || "").trim();
    if (!p) return toast.error("Add an image prompt first");
    setCoverBusy(true);
    const pending = toast.loading("Generating image…");
    try {
      const res = await generateAssetCover(asset.id, p, undefined, imageAspectForPlatform(asset.platform));
      res?.ok ? toast.success("Image generated", { id: pending }) : toast.error(`Failed: ${res?.message || "unknown error"}`, { id: pending });
    } finally { setCoverBusy(false); }
  };

  const onUploadCover = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file");
    if (file.size > 12 * 1024 * 1024) return toast.error("Image too large (max 12 MB)");
    const reader = new FileReader();
    reader.onload = async () => {
      setCoverBusy(true);
      const pending = toast.loading("Uploading image…");
      try {
        const res = await uploadAssetCover(asset.id, reader.result);
        res?.ok ? toast.success("Image uploaded", { id: pending }) : toast.error(`Failed: ${res?.message || "unknown error"}`, { id: pending });
      } finally { setCoverBusy(false); }
    };
    reader.readAsDataURL(file);
  };
  const [revisionMode, setRevisionMode] = useState("revise");
  const previewAsset = useMemo(() => makeAssetFromDraft(asset, draft), [asset, draft]);
  const charCount = [draft.hook, draft.body, draft.cta, draft.hashtags].filter(Boolean).join("\n").length;
  const overLimit = asset.char_limit && charCount > asset.char_limit;

  useEffect(() => {
    setDraft(makeDraftFromAsset(asset));
    setCoverPrompt(getAssetCoverPrompt(asset));
    setRevisionMode("revise");
  }, [asset]);

  const setField = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const persistDraft = (showToast = true) => {
    updateAsset(asset.id, {
      hook: draft.hook,
      body: draft.body,
      cta: draft.cta,
      hashtags: splitHashtags(draft.hashtags),
      char_count: charCount,
      status: "needs_review",
    });
    if (showToast) toast.success("Draft updated");
  };

  return (
    <motion.section
      ref={editorRef}
      tabIndex={-1}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="scroll-mt-24 border-b bg-card p-3 outline-none ring-[color:var(--accent-cyan)]/30 focus-visible:ring-2 sm:p-4"
      style={{ borderColor: "rgba(160, 168, 162,0.82)" }}
      data-testid="create-post-editor"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 rounded-full border bg-[color:var(--accent-blue)]/12 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--accent-cyan)]" style={{ borderColor: "rgba(18, 147, 79,0.18)" }}>
            <Pencil className="h-3.5 w-3.5" /> Editing selected post
          </p>
          <h3 className="mt-2 text-lg font-semibold tracking-[-0.02em] text-foreground">Tune this {asset.platform?.replace("_", " ")} post before approval</h3>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{asset.title}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClose} className="gap-1.5" data-testid="create-editor-close">
          <XIcon className="h-3.5 w-3.5" /> Close
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_410px]">
        <PlatformPreviewPanel
          asset={previewAsset}
          title="Live edit preview"
          note="Every change on the right updates this platform-native preview before approval."
          bodyClassName="bg-[color:var(--surface-3)]/80"
          className={cn("order-2 self-start xl:order-1", overLimit && "ring-2 ring-red-400/25")}
          mobilePreviewContext="editor"
        />

        <aside className="order-1 space-y-3 rounded-xl border bg-[#0C0F0E] p-3 text-white xl:order-2" style={{ borderColor: "rgba(18, 22, 20,0.18)" }}>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Hook</Label>
            <Input
              value={draft.hook}
              onChange={(e) => setField("hook", e.target.value)}
              className="border-white/10 bg-white/10 text-white placeholder:text-muted-foreground"
              data-testid="create-editor-hook"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Post body</Label>
            <Textarea
              value={draft.body}
              onChange={(e) => setField("body", e.target.value)}
              rows={8}
              className="min-h-[190px] resize-y border-white/10 bg-white/10 text-white placeholder:text-muted-foreground"
              data-testid="create-editor-body"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">CTA</Label>
            <Input
              value={draft.cta}
              onChange={(e) => setField("cta", e.target.value)}
              className="border-white/10 bg-white/10 text-white placeholder:text-muted-foreground"
              data-testid="create-editor-cta"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Hashtags</Label>
            <Input
              value={draft.hashtags}
              onChange={(e) => setField("hashtags", e.target.value)}
              className="border-white/10 bg-white/10 text-white placeholder:text-muted-foreground"
              placeholder="#AI #Agents"
              data-testid="create-editor-hashtags"
            />
          </div>

          {/* Image — change this post's cover (generate / upload / remove) */}
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">Image</Label>
            {asset.cover_image && (
              <div className="overflow-hidden rounded-lg border border-white/10">
                <img src={asset.cover_image} alt="Post cover" className="block max-h-[420px] w-full object-contain" />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/15 bg-white/10 text-white hover:bg-white/20" onClick={onGenerateCover} disabled={coverBusy} data-testid="create-editor-image-generate">
                {coverBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
                {asset.cover_image ? "Regenerate" : "Generate"}
              </Button>
              <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/15 bg-white/10 text-white hover:bg-white/20" onClick={() => coverInputRef.current?.click()} disabled={coverBusy} data-testid="create-editor-image-upload">
                <Upload className="h-3.5 w-3.5" /> Upload
              </Button>
              {asset.cover_image && (
                <Button type="button" size="sm" variant="ghost" className="gap-1.5 text-muted-foreground hover:text-red-300" onClick={() => updateAsset(asset.id, { cover_image: null })} disabled={coverBusy} data-testid="create-editor-image-remove">
                  <XIcon className="h-3.5 w-3.5" /> Remove
                </Button>
              )}
              <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={onUploadCover} data-testid="create-editor-image-input" />
            </div>
            <Textarea
              value={coverPrompt}
              onChange={(e) => setCoverPrompt(e.target.value)}
              rows={2}
              placeholder="Describe the image to generate…"
              className="resize-none border-white/10 bg-white/10 text-[12.5px] text-white placeholder:text-muted-foreground"
              data-testid="create-editor-image-prompt"
            />
          </div>

          {asset.platform === "instagram" && (
            <IgCardControl asset={asset} updateAsset={updateAsset} variant="dark" />
          )}

          <RevisionConsole
            asset={previewAsset}
            initialMode={revisionMode}
            onRevise={async ({ instruction, mode }) => {
              const res = await reviseAsset(asset.id, { instruction, mode });
              if (res?.ok && res.asset) setDraft(makeDraftFromAsset(res.asset));
              return res;
            }}
            dark
          />

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3">
            <Button type="button" onClick={() => persistDraft()} className="gap-1.5" data-testid="create-editor-save" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
              <Save className="h-3.5 w-3.5" /> Save edits
            </Button>
            <ApprovalActions
              compact
              showRevise={false}
              showRegenerate={false}
              testIdPrefix={`create-editor-approval-${asset.id}`}
              onApprove={() => { persistDraft(false); approveAsset(asset.id); toast.success("Approved"); }}
              onReject={() => { rejectAsset(asset.id); toast.error("Rejected"); }}
            />
          </div>
          {asset.status === "approved" || asset.status === "scheduled" ? (
            <div className="rounded-lg border bg-emerald-300/10 p-3" style={{ borderColor: "rgba(110,231,183,0.22)" }} data-testid={`create-editor-handoff-${asset.id}`}>
              <p className="text-[12px] font-semibold text-emerald-100">{asset.status === "scheduled" ? "Scheduled and saved to Library" : "Approved and saved to Library"}</p>
              <p className="mt-1 text-[11.5px] leading-relaxed text-emerald-100/75">The approved draft is now ready for scheduling, publishing workflow, or later reuse.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button asChild size="sm" variant="outline" className="gap-1.5 border-white/15 bg-white/10 text-white hover:bg-white/15" data-testid={`create-editor-open-library-${asset.id}`}>
                  <Link to={`/library/${asset.id}`}><FolderOpen className="h-3.5 w-3.5" /> Open in Library</Link>
                </Button>
                {asset.status !== "scheduled" && (
                  <ScheduleAssetDialog
                    asset={asset}
                    trigger={(
                      <Button type="button" size="sm" variant="outline" className="gap-1.5 border-white/15 bg-white/10 text-white hover:bg-white/15" data-testid={`create-editor-schedule-${asset.id}`}>
                        <CalendarPlus className="h-3.5 w-3.5" /> Schedule
                      </Button>
                    )}
                  />
                )}
                {asset.status === "scheduled" && (
                  <Button asChild size="sm" variant="outline" className="gap-1.5 border-white/15 bg-white/10 text-white hover:bg-white/15" data-testid={`create-editor-view-calendar-${asset.id}`}>
                    <Link to="/calendar"><CalendarPlus className="h-3.5 w-3.5" /> View Calendar</Link>
                  </Button>
                )}
                <Button type="button" size="sm" variant="ghost" onClick={() => copyGeneratedPost(asset)} className="gap-1.5 text-emerald-100 hover:bg-white/10 hover:text-white" data-testid={`create-editor-copy-${asset.id}`}>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </motion.section>
  );
}

function makeDraftFromAsset(asset) {
  return {
    hook: asset.hook && asset.hook !== "—" ? asset.hook : "",
    body: asset.body || "",
    cta: asset.cta && asset.cta !== "—" ? asset.cta : "",
    hashtags: Array.isArray(asset.hashtags) ? asset.hashtags.join(" ") : "",
  };
}

function makeAssetFromDraft(asset, draft) {
  const hashtags = splitHashtags(draft.hashtags);
  return {
    ...asset,
    hook: draft.hook,
    body: draft.body,
    cta: draft.cta,
    hashtags,
    char_count: [draft.hook, draft.body, draft.cta, draft.hashtags].filter(Boolean).join("\n").length,
  };
}

function splitHashtags(value) {
  return (value || "")
    .split(/[\s,]+/)
    .map((tag) => tag.trim())
    .filter(Boolean)
    .map((tag) => (tag.startsWith("#") ? tag : `#${tag}`));
}

function PostDecisionPanel({ asset, onCopy }) {
  const review = getPostReviewWorkspace(asset);
  const toneMap = {
    review: {
      Icon: Activity,
      className: "border-amber-300 bg-[color:var(--accent-amber)]/12 text-amber-800 dark:border-amber-500/30 dark:text-amber-200",
      iconClassName: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-[color:var(--accent-amber)]",
    },
    approved: {
      Icon: CheckCircle2,
      className: "border-emerald-300 bg-[color:var(--accent-green)]/12 text-emerald-800 dark:border-emerald-500/30 dark:text-emerald-200",
      iconClassName: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-[color:var(--accent-green)]",
    },
    scheduled: {
      Icon: CalendarPlus,
      className: "border-sky-300 bg-sky-500/12 text-sky-800 dark:border-sky-500/30 dark:text-sky-200",
      iconClassName: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
    },
    published: {
      Icon: CheckCircle2,
      className: "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]",
      iconClassName: "bg-[color:var(--surface-3)] text-muted-foreground",
    },
    archived: {
      Icon: Ban,
      className: "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]",
      iconClassName: "bg-[color:var(--surface-3)] text-muted-foreground",
    },
  };
  const cfg = toneMap[review.tone] || toneMap.review;
  const Icon = cfg.Icon;
  const canSchedule = review.status === "approved";
  const canViewCalendar = review.status === "scheduled";
  const showHandoffActions = ["approved", "scheduled", "archived"].includes(asset.status);

  return (
    <div className={cn("rounded-lg border p-3", cfg.className)} data-testid={`create-post-decision-${asset.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 gap-3">
          <span className={cn("mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md", cfg.iconClassName)}>
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold">{review.decisionTitle}</p>
            <p className="mt-0.5 text-[12px] leading-relaxed opacity-75">{review.decisionCopy}</p>
            <p className="mt-1 text-[11.5px] font-medium opacity-75">{review.actionHint}</p>
          </div>
        </div>
        {showHandoffActions && (
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild size="sm" variant="outline" className="gap-1.5 bg-white/80" data-testid={`create-open-library-${asset.id}`}>
              <Link to={`/library/${asset.id}`}><FolderOpen className="h-3.5 w-3.5" /> Open in Library</Link>
            </Button>
            {canSchedule && (
              <ScheduleAssetDialog
                asset={asset}
                trigger={(
                  <Button type="button" size="sm" className="gap-1.5 bg-[#0C0F0E] text-white hover:bg-slate-800" data-testid={`create-schedule-post-${asset.id}`}>
                    <CalendarPlus className="h-3.5 w-3.5" /> Schedule
                  </Button>
                )}
              />
            )}
            {canViewCalendar && (
              <Button asChild size="sm" className="gap-1.5 bg-[#0C0F0E] text-white hover:bg-slate-800" data-testid={`create-view-calendar-${asset.id}`}>
                <Link to="/calendar"><CalendarPlus className="h-3.5 w-3.5" /> View Calendar</Link>
              </Button>
            )}
            <Button type="button" size="sm" variant="ghost" onClick={onCopy} className="gap-1.5" data-testid={`create-copy-post-${asset.id}`}>
              <Copy className="h-3.5 w-3.5" /> Copy
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function GeneratedPostsPanel({ deliverables, editingAssetId, onEditAsset, updateAsset, approveAsset, rejectAsset }) {
  const { reviseAsset } = useAppState();
  const [selectedId, setSelectedId] = useState(null);
  const selectedAsset = deliverables.find((d) => d.id === selectedId) || deliverables[0] || null;

  if (deliverables.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-card p-8 text-center" style={{ borderColor: "rgba(18, 22, 20,0.14)" }} data-testid="create-run-empty-posts">
        <p className="text-sm font-medium text-foreground">Posts will appear here as the agent creates them.</p>
        <p className="mt-1 text-xs text-muted-foreground">The workflow above shows what is happening now.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:h-[74vh] xl:grid-cols-[minmax(0,1fr)_360px]" data-testid="create-run-posts">
      <div className="space-y-4 xl:min-h-0 xl:overflow-y-auto xl:pr-2 scrollbar-thin">
      {deliverables.map((asset, index) => (
        (() => {
          const isEditing = editingAssetId === asset.id;
          const isSelected = selectedAsset?.id === asset.id;
          const review = getPostReviewWorkspace(asset);
          const previewMeta = getPlatformPreviewMeta(asset);
          return (
        <article
          key={asset.id}
          onClick={() => setSelectedId(asset.id)}
          className={cn(
            "cursor-pointer overflow-hidden rounded-xl border bg-card shadow-[0_18px_42px_-36px_rgba(18, 22, 20,0.55)] transition-colors",
            isEditing
              ? "ring-2 ring-[color:var(--accent-cyan)]/35"
              : isSelected && "ring-2 ring-[color:var(--accent-blue)]/60",
          )}
          style={{ borderColor: isEditing || isSelected ? "rgba(18, 147, 79,0.34)" : "rgba(18, 22, 20,0.12)" }}
        >
          <div className="flex flex-wrap items-start justify-between gap-3 border-b bg-card px-3 py-3 sm:px-4" style={{ borderColor: "rgba(226,232,240,0.92)" }}>
            <div className="flex min-w-0 items-center gap-2">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#0C0F0E] font-mono text-[11px] text-white">
                {String(index + 1).padStart(2, "0")}
              </span>
              <PlatformBadge platform={asset.platform} compact />
              <div className="min-w-0">
                <h4 className="truncate text-sm font-semibold text-foreground">{asset.title}</h4>
                <p className="text-[11.5px] text-muted-foreground">
                  {asset.skill_used || "BrandVoice"} · {previewMeta.characterLabel}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <StatusBadge status={asset.status} />
              <Button
                type="button"
                variant={isEditing ? "default" : "outline"}
                size="sm"
                onClick={() => onEditAsset(asset.id)}
                aria-pressed={isEditing}
                className={cn("gap-1.5", isEditing ? "bg-[color:var(--accent-blue)] text-[color:var(--accent-blue-ink)] hover:bg-[color:var(--accent-blue)]" : "bg-card")}
                data-testid={`create-edit-post-${asset.id}`}
              >
                <Pencil className="h-3.5 w-3.5" /> {isEditing ? "Editing" : "Edit post"}
              </Button>
            </div>
          </div>
          <div className="bg-[color:var(--surface-3)]/70 p-3 sm:p-4">
            <PlatformPreviewPanel
              asset={asset}
              compact
              className="shadow-[0_18px_42px_-34px_rgba(18, 22, 20,0.55)]"
              bodyClassName="bg-[color:var(--surface-3)]/80"
              mobilePreviewContext="review"
            />
          </div>
          <div className="space-y-3 border-t bg-card px-3 py-3 sm:px-4" style={{ borderColor: "rgba(226,232,240,0.92)" }}>
            <PostDecisionPanel
              asset={asset}
              onCopy={() => copyGeneratedPost(asset)}
            />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[11.5px] font-medium text-muted-foreground">{review.actionHint}</p>
              <ApprovalActions
                testIdPrefix={`create-post-approval-${asset.id}`}
                showRegenerate={false}
                onApprove={() => { approveAsset(asset.id); toast.success("Approved and saved to Library"); }}
                onRevise={() => setSelectedId(asset.id)}
                onReject={() => { rejectAsset(asset.id); toast.error("Rejected"); }}
                showApprove={review.canApprove}
              />
            </div>
          </div>
        </article>
          );
        })()
      ))}
      </div>

      <aside className="xl:h-full xl:min-h-0">
        {selectedAsset ? (
          <ReviseAgentPanel
            asset={selectedAsset}
            className="xl:h-full"
            onRevise={(instruction) => reviseAsset(selectedAsset.id, { instruction, mode: "revise" })}
            onRegenerate={(instruction) => reviseAsset(selectedAsset.id, { instruction: instruction || "Fresh take — a new angle on the same idea", mode: "regenerate" })}
            onApprove={() => { approveAsset(selectedAsset.id); toast.success("Approved and saved to Library"); }}
          />
        ) : null}
      </aside>
    </div>
  );
}

function SupportingMaterialsPanel({ supporting }) {
  if (supporting.length === 0) {
    return (
      <div className="rounded-xl border border-dashed bg-[color:var(--surface-2)] p-8 text-center" style={{ borderColor: "rgba(18, 22, 20,0.14)" }}>
        <p className="text-sm font-medium text-foreground">No supporting materials for this run.</p>
        <p className="mt-1 text-xs text-muted-foreground">Sources show up here when web research runs — choose Quick Web Scan or Thorough Research in the run setup.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="create-run-materials">
      {supporting.map((asset) => (
        <article key={asset.id} className="rounded-xl border bg-card p-4 shadow-[0_14px_36px_-34px_rgba(18, 22, 20,0.55)]" style={{ borderColor: "rgba(18, 22, 20,0.12)" }}>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-1">{asset.asset_type === "research_brief" ? "Research notes" : "Image prompt"}</h3>
            <div className="flex items-center gap-2">
              {asset.asset_type === "image_prompt" && (
                <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => copySupportingText(asset.body, "Image prompt")}>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
              )}
              <StatusBadge status={asset.status} />
            </div>
          </div>
          {asset.cover_image && (
            <img src={asset.cover_image} alt="" className="mt-3 max-h-[420px] w-full rounded-lg object-contain" />
          )}
          <p className="mt-3 whitespace-pre-wrap text-[12.5px] leading-[1.65] text-foreground/85">{asset.body}</p>
        </article>
      ))}
    </div>
  );
}

function BrandLogo({ brand, color = "currentColor" }) {
  const common = { className: "h-5 w-5", viewBox: "0 0 24 24", fill: color, "aria-hidden": true };
  if (brand === "linkedin") {
    return (
      <svg {...common}>
        <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.32 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.02H3.54V9H7.1v11.45Z" />
      </svg>
    );
  }
  if (brand === "x") {
    return (
      <svg {...common}>
        <path d="M18.9 3h3.28l-7.16 8.18 8.42 9.82h-6.6l-5.17-6.03L5.76 21H2.48l7.65-8.74L2.05 3h6.76l4.67 5.44L18.9 3Zm-1.15 16.27h1.82L7.82 4.64H5.87l11.88 14.63Z" />
      </svg>
    );
  }
  if (brand === "youtube") {
    return (
      <svg {...common}>
        <path d="M23.5 7.2a3 3 0 0 0-2.1-2.12C19.55 4.58 12 4.58 12 4.58s-7.55 0-9.4.5A3 3 0 0 0 .5 7.2 31.2 31.2 0 0 0 0 12a31.2 31.2 0 0 0 .5 4.8 3 3 0 0 0 2.1 2.12c1.85.5 9.4.5 9.4.5s7.55 0 9.4-.5a3 3 0 0 0 2.1-2.12A31.2 31.2 0 0 0 24 12a31.2 31.2 0 0 0-.5-4.8ZM9.55 15.5v-7l6.27 3.5-6.27 3.5Z" />
      </svg>
    );
  }
  if (brand === "instagram") {
    return (
      <svg {...common}>
        <path d="M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2Zm-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6A3.6 3.6 0 0 0 16.4 4H7.6Zm9.15 2.1a1.35 1.35 0 1 1 0 2.7 1.35 1.35 0 0 1 0-2.7ZM12 7.3a4.7 4.7 0 1 1 0 9.4 4.7 4.7 0 0 1 0-9.4Zm0 2a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4Z" />
      </svg>
    );
  }
  if (brand === "threads") {
    return (
      <svg {...common}>
        <path d="M17.8 11.18c-.18-3.3-1.98-5.16-5.02-5.18-1.8-.01-3.38.76-4.34 2.1l1.7 1.16c.68-.95 1.78-1.16 2.57-1.15 1 .01 1.75.3 2.24.87.35.41.59.97.71 1.66-.9-.15-1.88-.2-2.93-.14-2.96.17-4.86 1.9-4.73 4.3.07 1.22.68 2.27 1.73 2.94.88.57 2.02.84 3.2.78 1.56-.09 2.78-.68 3.63-1.76.65-.82 1.06-1.88 1.24-3.22.73.44 1.27 1.02 1.57 1.72.52 1.2.55 3.16-1.06 4.76-1.42 1.41-3.12 2.02-5.68 2.04-2.84-.02-4.99-.93-6.4-2.71-1.32-1.67-2-4.08-2.02-7.17.02-3.09.7-5.5 2.02-7.17C7.6 3.23 9.75 2.32 12.59 2.3c2.86.02 5.05.94 6.5 2.74.71.88 1.25 1.98 1.62 3.27l2.09-.56c-.45-1.6-1.14-2.97-2.06-4.1C18.86 1.33 16.12.15 12.6.13h-.01C9.08.15 6.37 1.34 4.55 3.66 2.93 5.7 2.1 8.57 2.08 12.18c.02 3.61.85 6.48 2.47 8.53 1.82 2.31 4.53 3.5 8.04 3.52h.01c3.12-.02 5.34-.84 7.2-2.68 2.43-2.41 2.36-5.43 1.56-7.28-.58-1.35-1.8-2.4-3.56-3.09Zm-4.98 5.23c-1.31.07-2.67-.51-2.74-1.73-.05-.9.64-1.9 2.77-2.02.24-.02.48-.02.71-.02.78 0 1.51.08 2.17.24-.25 3.08-1.7 3.47-2.91 3.53Z" />
      </svg>
    );
  }
  if (brand === "facebook") {
    return (
      <svg {...common}>
        <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.03 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07Z" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12.02 3.3c2.02-1.52 4.74-1.46 6.47.16 1.8 1.68 1.93 4.39.32 6.45 1.74.48 3.03 2.05 3.03 3.94 0 2.25-1.83 4.08-4.08 4.08H6.34a4.18 4.18 0 0 1-4.18-4.18c0-1.92 1.3-3.6 3.15-4.06-.4-2.1.55-4.3 2.49-5.56 1.31-.85 2.86-1.08 4.22-.83Z" />
    </svg>
  );
}

// Rendered as a clickable div (not a <button>) because it can contain its own
// interactive children — e.g. the "Clear image" button and the file input.
// Nesting a <button> inside a <button> is invalid HTML and causes hydration
// errors, so this uses role="button" + keyboard handlers instead.
function ImageOption({ active, onClick, onDragOver, onDrop, testId, icon: Icon, accent, label, hint, children, dark = false }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(e); } }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={testId}
      className={cn(
        "group relative cursor-pointer overflow-hidden rounded-xl border p-3 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-cyan)]/50",
        dark
          ? active
            ? "border-[color:var(--accent-cyan)]/40 bg-card text-foreground shadow-[0_0_0_1px_rgba(255,255,255,0.5),0_18px_42px_-24px_rgba(143, 224, 74,0.95)]"
            : "border-white/12 bg-white/[0.075] text-slate-200 hover:border-white/24 hover:bg-white/[0.12]"
          : active ? "border-[color:var(--accent-blue)] bg-[color:var(--accent-blue)]/12 shadow-[0_0_0_1px_rgba(18, 147, 79,0.12)]" : "hover:border-[color:var(--accent-blue)]/40 hover:bg-[color:var(--surface-2)]",
      )}
      style={dark ? undefined : { borderColor: active ? undefined : "var(--border-subtle)", backgroundColor: active ? undefined : "var(--surface-1)" }}
    >
      {active && (
        <span aria-hidden className="absolute right-2 top-2 inline-flex h-5 w-5 items-center justify-center rounded-full" style={{ backgroundColor: accent, color: "#FFFFFF" }}>
          <CheckCircle2 className="h-3.5 w-3.5" />
        </span>
      )}
      <Icon className="h-5 w-5" style={{ color: active ? accent : dark ? "#E2E8F0" : "#64748B" }} />
      <p className={cn("mt-2 text-sm font-semibold", dark ? active ? "text-foreground" : "text-white" : "text-1")}>{label}</p>
      <p className={cn("text-[11px] leading-snug", dark ? active ? "text-muted-foreground" : "text-muted-foreground" : "text-muted-foreground")}>{hint}</p>
      {children}
    </div>
  );
}
